import { holdsInvite } from '@alphadesk/ontology/objects/member.ts';
import { PATH_INVITES, reachedCount } from '@alphadesk/ontology/objects/path.ts';
import { isPending } from '@alphadesk/ontology/objects/review.ts';
import { andList } from '@alphadesk/ontology/primitives.ts';
import {
  API_PATHS,
  type Invites,
  invitesSchema,
  type PathRead,
  type Viewer,
} from '@alphadesk/web/contract.ts';
import { PAGES } from '@alphadesk/web/links.ts';
import clsx from 'clsx';
import { Gift, Mail, Share2 } from 'lucide-react';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { ask, useToastedWrite } from '../lib/act.ts';
import { note } from '../lib/posthog.ts';
import { type Read as Reading, useRead } from '../lib/reads.ts';
import { forgetQuery, navigate, useLinkClick } from '../lib/router.ts';
import { PathList } from './Path.tsx';
import { PageSection } from './pages/Sections.tsx';
import { CopyButton } from './primitives/button.tsx';
import { linkClass } from './primitives/controls.tsx';
import { Avatar } from './primitives/marks.tsx';
import { Read } from './primitives/read.tsx';
import { Card, Hint } from './primitives/shell.tsx';
import { Confirm } from './primitives/ticket.tsx';
import { useToast } from './Toast.tsx';

interface InviteState {
  offered: boolean;
  read: Reading<Invites> | null;
  /** An admin viewing as the member, who may change nothing. */
  viewing: boolean;
}

const NONE: InviteState = { offered: false, read: null, viewing: false };

const InviteContext = createContext<InviteState>(NONE);

const useInvite = (): InviteState => useContext(InviteContext);

/** The member's path as last read (null for an admin, or until read), and how to read it again. */
export function usePath(): { path: PathRead | null; viewing: boolean; refresh: () => void } {
  const { read, viewing } = useInvite();
  const path = read?.state.status === 'ok' ? read.state.data.path : null;
  return { path, viewing, refresh: read?.refresh ?? noop };
}

const noop = () => {};

const AT = `${PAGES.account}#invite`;

export function InviteProvider({
  viewer,
  viewing,
  children,
}: {
  viewer: Viewer;
  viewing: boolean;
  children: ReactNode;
}) {
  const offered = viewer.memberId !== null;
  // A notification's "Invite another" lands on `?invite=open`; the invite lives on the account page.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('invite') !== 'open') return;
    if (offered) navigate(AT, { replace: true });
    else forgetQuery(['invite']);
  }, [offered]);
  // Read once for the header's count, and again each time the account page shows it.
  const read = useRead(offered ? API_PATHS.invites : null, invitesSchema);
  const state = useMemo(
    () => (offered ? { offered, read, viewing } : NONE),
    [offered, read, viewing],
  );
  return <InviteContext.Provider value={state}>{children}</InviteContext.Provider>;
}

/** Counts invites to give, or, with none, how far along their path the member is. */
export function InvitePill() {
  const { offered, read } = useInvite();
  const onClick = useLinkClick();
  if (!offered || read === null) return null;
  const invites = read.state.status === 'ok' ? read.state.data : null;
  const left = invites?.left ?? null;
  const path = invites?.path ?? null;
  const along = left === 0 && path !== null && !path.admits ? reachedCount(path) : null;
  const label =
    left === null
      ? 'Invite a friend'
      : along !== null
        ? `Invite a friend — ${along} of ${PATH_INVITES} steps to your invites`
        : `Invite a friend — ${left} invites left`;
  return (
    <a
      href={AT}
      onClick={onClick}
      aria-label={label}
      title={
        along !== null
          ? 'Each step of your path earns an invite'
          : path?.admits
            ? 'Your invite lets a friend straight in'
            : 'Vouch for a friend: their request reaches the admins with your name on it'
      }
      className="hidden items-center gap-1.5 rounded-control border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-medium text-accent transition-colors hover:border-accent/70 hover:bg-accent/20 sm:inline-flex"
    >
      <Gift aria-hidden className="size-3.5" />
      Invite
      {left !== null && left > 0 && (
        <span className="tnum -mr-1 rounded-full bg-accent/20 px-1.5 text-3xs leading-4">
          {left}
        </span>
      )}
      {along !== null && (
        <span className="tnum -mr-1 text-3xs leading-4 text-accent/70">
          {along}/{PATH_INVITES}
        </span>
      )}
    </a>
  );
}

function blurbOf(invites: Invites): string {
  const { left, allowance, path } = invites;
  if (allowance === 0) {
    return 'Invites are earned on your path: each step shows you something the desk does, so you can tell a friend what it does for you. Until then your link still takes requests, without your name.';
  }
  if (left === 0) {
    return path.admits
      ? 'Your invites are in use. A friend you brought in who finishes their own path earns you another, and one comes back if an admin turns a friend down.'
      : 'Your invites are in use. Your next step earns another, and one comes back if an admin turns a friend down. Until then your link still takes requests, without your name.';
  }
  return path.admits
    ? 'Your path is done, so your invite lets a friend straight in, with no wait for the admins.'
    : 'An invite puts a friend’s request at the top of the admins’ list, with your name on it. Each step of your path earns another.';
}

/** On the account page, at `#invite`. */
export function InviteSection() {
  const { offered, read } = useInvite();
  const refresh = read?.refresh;
  useEffect(() => refresh?.(), [refresh]);
  if (!offered || read === null) return null;
  const invites = read.state.status === 'ok' ? read.state.data : null;
  const left = invites?.left ?? null;
  return (
    <PageSection
      id="invite"
      title="Invite a friend"
      blurb={
        invites === null
          ? 'Each step of your path earns an invite, so you can tell a friend what the desk does for you.'
          : blurbOf(invites)
      }
      status={
        invites === null
          ? null
          : invites.allowance === 0
            ? { tone: 'off', label: `0 of ${PATH_INVITES} earned` }
            : { tone: (left ?? 0) > 0 ? 'good' : 'off', label: `${left} left` }
      }
    >
      <Card>
        <Read state={read.state} refresh={read.refresh}>
          {(loaded) => <Loaded invites={loaded} onChanged={read.refresh} />}
        </Read>
      </Card>
    </PageSection>
  );
}

function Loaded({ invites, onChanged }: { invites: Invites; onChanged: () => void }) {
  const earning = invites.allowance === 0 && !invites.invited.some(holdsInvite);
  return (
    <>
      {earning && <PathList path={invites.path} />}
      <LinkBox link={invites.link} />
      <Pitch pitch={invites.pitch} />
      <ShareTargets invites={invites} />
      {!earning && <Seats invites={invites} />}
      {!earning && <PathList path={invites.path} />}
      <Reset onReset={onChanged} />
    </>
  );
}

function LinkBox({ link }: { link: string }) {
  const shown = link.replace(/^https?:\/\//, '');
  return (
    <div className="flex items-center gap-1.5 rounded-control border border-line bg-surface p-1 pl-2.5">
      <input
        readOnly
        // The scheme is what truncation would keep and the code what it would cut; Copy sends it whole.
        value={shown}
        aria-label="Your invite link"
        onFocus={(event) => event.currentTarget.select()}
        className="ph-no-capture min-w-0 flex-1 truncate bg-transparent font-mono text-xs text-ink outline-none"
      />
      <span onClickCapture={() => note('invite shared', { via: 'copy' })}>
        <CopyButton text={link} label="Copy link" />
      </span>
    </div>
  );
}

function Pitch({ pitch }: { pitch: string }) {
  return (
    <figure className="flex flex-col gap-1">
      <figcaption className="text-2xs font-medium text-ink-faint">Goes with your link</figcaption>
      <blockquote className="ph-no-capture border-l-2 border-line pl-2.5 text-xs leading-5 text-ink-muted">
        {pitch}
      </blockquote>
    </figure>
  );
}

const SUBJECT = 'Your invite to AlphaDesk';

/** The official marks, unaltered, from each company's brand kit (web/public/brands). */
const brandMark = (file: string) => (
  <img src={`/brands/${file}`} alt="" aria-hidden className="size-5" />
);

function ShareTargets({ invites }: { invites: Invites }) {
  const toast = useToast();
  const { link, pitch: message } = invites;
  const native = typeof navigator.share === 'function';
  const targets = [
    {
      via: 'whatsapp',
      label: 'WhatsApp',
      icon: brandMark('whatsapp.svg'),
      href: `https://wa.me/?text=${encodeURIComponent(`${message} ${link}`)}`,
    },
    {
      via: 'telegram',
      label: 'Telegram',
      icon: brandMark('telegram.svg'),
      href: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(message)}`,
    },
    {
      via: 'email',
      label: 'Email',
      icon: <Mail aria-hidden className="size-5" />,
      href: `mailto:?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(`${message}\n\n${link}`)}`,
    },
  ];
  const cell =
    'flex flex-col items-center justify-center gap-1 rounded-control border border-line-soft py-2 text-2xs font-medium text-ink-muted transition-colors hover:border-line hover:bg-surface hover:text-ink touch:py-2.5';

  return (
    <div className={clsx('grid gap-1.5', native ? 'grid-cols-4' : 'grid-cols-3')}>
      {targets.map((target) => (
        <a
          key={target.via}
          href={target.href}
          target="_blank"
          rel="noreferrer"
          onClick={() => note('invite shared', { via: target.via })}
          className={cell}
        >
          {target.icon}
          {target.label}
        </a>
      ))}
      {native && (
        <button
          type="button"
          className={cell}
          onClick={() => {
            note('invite shared', { via: 'sheet' });
            // A dismissed share sheet rejects with AbortError; that is not a failure.
            navigator
              .share({ title: SUBJECT, text: message, url: link })
              .catch((error: unknown) => {
                if (error instanceof DOMException && error.name === 'AbortError') return;
                toast.refused([
                  'Your browser would not open the share sheet. Copy the link instead.',
                ]);
              });
          }}
        >
          <Share2 aria-hidden className="size-5" />
          More
        </button>
      )}
    </div>
  );
}

type Invited = Invites['invited'][number];

const inAlready = (person: Invited): boolean => holdsInvite(person) && !isPending(person.status);

function Seats({ invites }: { invites: Invites }) {
  // In before waiting, so the row fills from the left in the order that matters.
  const holding = invites.invited
    .filter(holdsInvite)
    .sort((a, b) => Number(inAlready(b)) - Number(inAlready(a)));
  // A seat in use is never taken back, even past what the path earned.
  const seats = Array.from(
    { length: Math.max(invites.allowance, holding.length) },
    (_, index) => holding[index] ?? null,
  );
  return (
    <section className="flex flex-col gap-2 border-t border-line-soft pt-3">
      <div className="flex items-center justify-between gap-3">
        <ol aria-label="Your invites" className="flex flex-wrap items-center gap-1.5">
          {seats.map((person, index) => (
            <li
              // Seats are positions, not people: the order never changes.
              // biome-ignore lint/suspicious/noArrayIndexKey: see above
              key={index}
              title={
                person
                  ? `${person.name}, ${inAlready(person) ? 'on the desk' : 'waiting for an admin'}`
                  : 'Unused'
              }
            >
              {person ? (
                <Avatar
                  picture={person.picture}
                  name={person.name}
                  className={clsx(
                    'ph-no-capture size-6 text-3xs ring-2',
                    inAlready(person) ? 'ring-accent/70' : 'ring-warn/70',
                  )}
                />
              ) : (
                <span className="block size-6 rounded-full border border-dashed border-line" />
              )}
            </li>
          ))}
        </ol>
        <span className="tnum shrink-0 text-xs text-ink-muted">
          <span className="font-semibold text-ink">{invites.left}</span> of {invites.allowance} left
        </span>
      </div>
      {invites.invited.length > 0 && (
        <Hint faint className="ph-no-capture">
          {invitedLine(invites.invited)}
        </Hint>
      )}
    </section>
  );
}

function invitedLine(invited: readonly Invited[]): string {
  const names = (people: readonly Invited[]) => andList(people.map((person) => person.name));
  const inside = invited.filter(inAlready);
  const waiting = invited.filter((person) => isPending(person.status));
  const declined = invited.filter((person) => !holdsInvite(person));
  const are = (people: readonly Invited[]) => (people.length === 1 ? 'is' : 'are');
  return [
    inside.length > 0 && `${names(inside)} ${are(inside)} in.`,
    waiting.length > 0 && `${names(waiting)} ${are(waiting)} waiting for an admin.`,
    declined.length > 0 &&
      `An admin turned down ${names(declined)}; ${declined.length === 1 ? 'that invite' : 'those invites'} came back to you.`,
  ]
    .filter(Boolean)
    .join(' ');
}

function Reset({ onReset }: { onReset: () => void }) {
  const { run, busy } = useToastedWrite();
  const [asking, setAsking] = useState(false);

  const reset = async () => {
    const answer = await run(() => ask(API_PATHS.invites, {}, invitesSchema));
    setAsking(false);
    if (answer.ok) onReset();
  };

  return asking ? (
    <Confirm
      tone="warn"
      said="Your current link will stop carrying your name. Requests already made keep it, and invites in use stay in use."
      keep="Keep this link"
      onKeep={() => setAsking(false)}
      act="Make a new link"
      onAct={() => void reset()}
      busy={busy}
    />
  ) : (
    <button
      type="button"
      onClick={() => setAsking(true)}
      className={linkClass('faint', 'self-start')}
    >
      Link sent to the wrong place? Make a new one
    </button>
  );
}
